import { Box, Group, Stack } from '@mantine/core';
import {
	Selector,
	SelectorTarget,
	SelectType,
	SettingsBlock,
} from '@repo/common/components/index.ts';
import { SettingsMultiSelect } from '@repo/common/components/SettingsMultiSelect/SettingsMultiSelect.tsx';
import { pluralize } from '@repo/common/utils/index.ts';
import { Text, Title } from '@repo/foundations';
import { upperFirst } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import {
	useSuspenseFeatureAccess,
	useUpdateIntegration,
	useUserGroupList,
	type IIntegration,
} from '../../../../../api';
import { useExtendedUserList } from '../../../../../api/hooks/user/useExtendedUserList.ts';
import { useFuseSearchCallback } from '../../../../../hooks/useFuseSearchCallback.ts';

interface QueryPermissionsProps {
	integration: IIntegration;
}

export function QueryPermissions({ integration }: QueryPermissionsProps) {
	const { activeUsers } = useExtendedUserList();
	const { data: groups } = useUserGroupList({});
	const { mutateAsync: updateIntegration } = useUpdateIntegration({});
	const { groupAccess } = useSuspenseFeatureAccess();

	const getFilteredUsers = useFuseSearchCallback({
		items: activeUsers ?? [],
		keys: ['email'],
		options: { threshold: 0.3 },
	});

	const getFilteredUsersItems = useCallback(
		async (searchTerm: string) => {
			const filteredServiceAccounts = getFilteredUsers(searchTerm);
			return filteredServiceAccounts.map((user) => ({
				value: user.id,
				label: user.email,
			}));
		},
		[getFilteredUsers]
	);

	const handleUserChange = useCallback(
		(values: string[]) => {
			updateIntegration({
				data: {
					id: integration.id,
					execute_access: values,
				},
			});
		},
		[integration.id, updateIntegration]
	);

	const handleGroupAccessChange = useCallback(
		(groupList: string[]) => {
			// debugger;
			updateIntegration({
				data: {
					id: integration.id,
					execute_access_groups: groupList,
				},
			});
		},
		[integration.id, updateIntegration]
	);

	const groupItems = useMemo(
		() =>
			(groups?.results || []).map((group) => ({
				value: group.id,
				label: group.name,
			})),
		[groups]
	);

	const usersValues = useMemo(
		() =>
			activeUsers
				?.filter((user) => integration.execute_access?.includes(user.id))
				?.map((user) => user.id),
		[activeUsers, integration.execute_access]
	);

	return (
		<Stack spacing="md">
			<Stack spacing={0}>
				<Title size="md">Query access</Title>
				<Text size="sm" color="text/secondary/default">
					Choose which roles and groups can use query features with this
					integration
				</Text>
			</Stack>
			<Box>
				<SettingsBlock
					title="Editors"
					description={`Select which editors can execute read-only queries through the ${upperFirst(integration.type)} integration`}
					rightComponent={
						<Selector
							initialValue={usersValues ?? []}
							getItems={getFilteredUsersItems}
							onChange={handleUserChange}
							selectType={SelectType.Multiple}
							disableSearch={false}
							disabled={false}
						>
							<SelectorTarget variant="tertiary">
								<Group spacing="2xs">
									<Text size="sm">
										{pluralize('editor', usersValues?.length, true)}
									</Text>
								</Group>
							</SelectorTarget>
						</Selector>
					}
				/>
				{groupAccess && (
					<SettingsMultiSelect
						searchable
						title="Groups"
						description={`Select which groups can execute read-only queries through the ${upperFirst(integration.type)} integration`}
						options={groupItems}
						values={integration.execute_access_groups ?? []}
						onChange={(values) => {
							handleGroupAccessChange(values);
						}}
						itemName="group"
					/>
				)}
			</Box>
		</Stack>
	);
}
